'use client';

/**
 * Global Error Boundary Component
 * 
 * Catches errors in the entire application and reports them to the API.
 * This component should wrap the root of the application to catch all unhandled errors.
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { reportError } from '@/utils/errorReporting';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class GlobalErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    console.log('✅ GlobalErrorBoundary: Component mounted');
  }

  componentDidMount() {
    console.log('✅ GlobalErrorBoundary: Component did mount');
    console.log('✅ GlobalErrorBoundary: Test functions available:');
    console.log('   - window.testErrorReporting() - Test error reporting directly');
    console.log('   - window.testErrorBoundary() - Test error boundary (throws error)');
    
    // Make test function available globally
    if (typeof window !== 'undefined') {
      (window as any).testErrorBoundary = () => {
        console.log('🧪 Testing error boundary...');
        throw new Error('Test error from error boundary');
      };
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Don't update state to show error UI - errors are reported silently
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console
    console.error('🔴 GlobalErrorBoundary: componentDidCatch triggered!');
    console.error('Global Error Boundary - Unhandled Error:', error);
    console.error('Error Info:', errorInfo);

    // Report error to API - use void to explicitly handle the promise
    void (async () => {
      try {
        console.log('GlobalErrorBoundary: Attempting to report error to API...');
        await reportError({
          error,
          context: 'Global Error Boundary',
          metadata: {
            componentStack: errorInfo.componentStack,
            errorName: error.name,
            errorBoundary: 'GlobalErrorBoundary',
          },
          showToast: false, // Don't show toast - errors are reported silently without UI
        });
        console.log('GlobalErrorBoundary: Error successfully reported to API');
      } catch (reportingError) {
        console.error('GlobalErrorBoundary: Failed to report error to API:', reportingError);
        console.error('GlobalErrorBoundary: Reporting error details:', {
          error: error.message,
          stack: error.stack,
          reportingError,
        });
      }
    })();

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    // Always render children - errors are caught and reported silently to API
    // No error UI is displayed to users
    return this.props.children;
  }
}

// Wrapper component to use hooks if needed in the future
export default function GlobalErrorBoundary({ children }: Props) {
  return <GlobalErrorBoundaryClass>{children}</GlobalErrorBoundaryClass>;
}
